<!-- Create button (fixed bottom right): label and action change with the page; hidden where there is nothing to create.
     It never covers the footer: when the footer reaches the button's height the button rises above it, with a single fixed
     jump (following the footer pixel by pixel while scrolling was "too much movement"). Id used by the guided tour. -->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { usePage } from '../composables/usePage'
import { useI18n } from '../composables/useI18n'
import { openVideo } from '../stores/video'
import type { PageId } from '../navigation'
import { useDialogsStore } from '../stores/dialogs'
import { useCreatorsStore } from '../stores/creators'
import { useSavedStore } from '../stores/saved'

const { t } = useI18n()
const { page } = usePage()
const dialogs = useDialogsStore()
const creators = useCreatorsStore()
const ACTIONS: Partial<Record<PageId, [string, () => void]>> = {
  vault: [t('Add hook'), () => dialogs.show('hook', { id: null })],
  ped: [t('New Video'), () => openVideo()],
  calendar: [t('New Video'), () => openVideo()],
  timeline: [t('New Video'), () => openVideo()],
  creators: [t('New creator'), () => creators.openEdit()],
  sources: [t('New source'), () => dialogs.show('source', { id: null })],
  saved: [t('Save a video'), () => dialogs.show('saved', useSavedStore().newArgs())]
}
const action = computed(() => (page.value ? ACTIONS[page.value] : undefined))

const btn = ref<HTMLButtonElement | null>(null)
const lift = ref(0)
let timer = 0
function measure() {
  timer = 0
  const fab = btn.value
  const footer = page.value ? document.querySelector(`#view-${page.value} > .app-footer`) : null
  let next = 0
  if (fab && footer && action.value) {
    // Base position of the (fixed) button: offsetTop/Left ignore "translate", so the measure doesn't change while it rises
    const aTop = fab.offsetTop, aBottom = aTop + fab.offsetHeight, aLeft = fab.offsetLeft, aRight = aLeft + fab.offsetWidth
    const parts = [...footer.children].map(c => c.getBoundingClientRect()).filter(r => r.width)
    if (parts.length) {
      const top = Math.min(...parts.map(r => r.top)), left = Math.min(...parts.map(r => r.left)), right = Math.max(...parts.map(r => r.right))
      // the height needed when the page is at the bottom, applied as soon as the footer touches the button area
      const view = footer.parentElement!
      const viewBottom = view.getBoundingClientRect().bottom - (parseFloat(getComputedStyle(view).paddingBottom) || 0)
      const topAtEnd = viewBottom - (footer.getBoundingClientRect().bottom - top)
      if (top < innerHeight && top < aBottom + 10 && left < aRight && right > aLeft) next = Math.max(0, Math.round(aBottom + 10 - topAtEnd))
    }
  }
  lift.value = next
}
const soon = () => { if (!timer) timer = window.setTimeout(measure, 16) }
let interval = 0
onMounted(() => {
  document.addEventListener('scroll', soon, true)
  window.addEventListener('resize', soon)
  interval = window.setInterval(() => { if (!document.hidden) soon() }, 300)
})
onBeforeUnmount(() => {
  document.removeEventListener('scroll', soon, true)
  window.removeEventListener('resize', soon)
  clearInterval(interval)
})
// A new page: measure again while its content settles
watch(page, async () => { await nextTick(); [0, 120, 350, 700].forEach(ms => setTimeout(soon, ms)) })
</script>

<template>
  <button v-show="action" id="fabBtn" ref="btn" :style="lift ? { translate: `0 ${-lift}px` } : undefined"
    class="fixed bottom-5 md:bottom-6 right-4 md:right-8 z-30 bg-brand hover:bg-brandDark text-white px-6 py-3.5 rounded-full font-bold shadow-xl flex items-center space-x-2 transition-transform active:scale-95"
    @click="action?.[1]()">
    <i class="fas fa-plus"></i> <span id="fabLabel">{{ action?.[0] }}</span>
  </button>
</template>
